/**
 * Stable color palette for listings within a comparison view.
 *
 * Up to 4 slots, colors chosen to be:
 *   - distinguishable on both light and dark backgrounds
 *   - already present in the existing Tailwind config (no new entries
 *     needed in tailwind.config)
 *   - emotionally neutral so they don't pre-bias the user toward a
 *     "red = bad / green = good" read before they look at the data
 *
 * Index is stable across a session - the color at position `i` in
 * the results array drives the trend line, the SHAP bar, the card
 * ring, and the table column dot for that listing. Passing it down
 * as a prop (instead of deriving per-component) means a listing
 * cannot accidentally switch color between the summary chips and
 * the trend chart.
 *
 * Hex values are used directly where ApexCharts or inline style
 * needs them; Tailwind utility classes (ring / bg / text / border)
 * are exposed separately for HTML rendering.
 */
export interface ListingPalette {
  /** Raw hex - used for chart colors (ApexCharts, canvas, etc). */
  hex: string;
  /** 1-stroke ring color class, e.g. "ring-indigo-500". */
  ring: string;
  /** Mid-tone bg + text pair for "badge" chips. */
  chipBg: string;
  chipText: string;
  /** Solid bg for a color swatch dot. */
  dot: string;
  /** Label text color on a card hero. */
  label: string;
}

// Emerald + amber were pulled out of this palette on 2026-04-23:
// emerald reads as "deal / good" everywhere else in the product, and
// amber is now the "best overall" winner color (matches ComparisonTable
// cell highlight). Listing slots use cool neutrals that don't pre-bias
// the user toward any verdict.
// Palette: indigo / gray / amber / cyan (the first two are the product's
// own accent and neutral). Green and red are deliberately excluded - green is
// reserved for "winner / best deal" cell highlights in the table
// (and for the "Great Deal" verdict chip), and rose/red is reserved
// for the "worst on this metric" highlight. Keeping the listing
// palette orthogonal to both means an Elan's color never
// accidentally looks like a verdict signal.
//
// Shade choices:
//   hex     : -600 (chart strokes / dots - strong on white)
//   ring    : -500 (card ring on hover - slightly softer)
//   chipBg  : -100 (light pill background)
//   chipText: -700 (dark text on the pill - works on dark mode too
//             because the light pill stays bright against gray-900)
//   dot     : -500 (palette swatch next to "Elan N")
const COMPARE_PALETTE: ListingPalette[] = [
  {
    hex: "#4f46e5",                // indigo-600 (the accent)
    ring: "ring-indigo-500",
    chipBg: "bg-indigo-100",
    chipText: "text-indigo-600 dark:text-indigo-400",
    dot: "bg-indigo-500",
    label: "text-indigo-600 dark:text-indigo-400",
  },
  {
    // The second slot was magenta-pink, a hue outside the product's
    // semantic scale (wave 3, 2026-10-25); it is the gray tokens now,
    // which flip with the theme in globals.css.
    hex: "#6b7280",                // gray-500 (reads on white and on the dark paper)
    ring: "ring-gray-400",
    chipBg: "bg-gray-100",
    chipText: "text-gray-700",
    dot: "bg-gray-500",
    label: "text-gray-700",
  },
  {
    hex: "#d97706",                // amber-600 (warm gold)
    ring: "ring-amber-500",
    chipBg: "bg-amber-100",
    chipText: "text-amber-700",
    dot: "bg-amber-500",
    label: "text-amber-700",
  },
  {
    hex: "#0891b2",                // cyan-600 (teal-cyan)
    ring: "ring-cyan-500",
    chipBg: "bg-cyan-100",
    chipText: "text-cyan-700",
    dot: "bg-cyan-500",
    label: "text-cyan-700",
  },
];

export function colorForIndex(i: number): ListingPalette {
  return COMPARE_PALETTE[i % COMPARE_PALETTE.length];
}
